"use client";

import { useSocketContext } from "@/components/providers/SocketProvider/SocketProvider";
import { WEBSOCKET_EVENTS } from "@/constants/websocket";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChatConversationSchemaType, ChatMessagesUnreadCountSchemaType, ConversationCreatedSchemaType, MessageSchemaType, NewMessageSchemaType, UserTypingSchemaType } from "../../api/schemas";
import { useConversationById, USER_CONVERSATION_BYID__QUERY_KEYS } from "../../api/queries/useConversationById";
import { uploadChatImagesAction } from "../../api/actions";
import { MAX_IMAGE_SIZE } from "@/constants/images";
import useSession from "@/hooks/QueryHooks/useSession";
import { useQueryClient } from "@tanstack/react-query";
import { Query } from "@/types/queryTpes";
import { USER_CONVERSATIONS_QUERY_KEYS } from "../../api/queries/useUserConversations";
import { USER_CHAT_UNREAD_COUNT_QUERY_KEYS } from "../../api/queries/useUserChatUnreadCount";
import { useAuth } from "@/components/providers/AuthProvider/AuthProvider";
import { useRouter } from "next/navigation";
import { useModal } from "@/components/providers/modal-provider/ModalProvider";
import { useKeyboardViewport } from "@/hooks/use-keyboard-viewport";
type Props = {
  conversationId?: string;
  recipientId?: string;
  realEstateId?: string;
  /** Skip the one-message-until-reply rule (used for friend chats) */
  isFriend?: boolean;
};
const TYPING_STOP_DELAY = 500;
const LOAD_MORE_SCROLL_TRIGGER_THRESHOLD = 200;
const SCROLL_DOWN_THRESHOLD_TO_SHOW_BUTTON = 150;
export function useUserConversationChat({
  conversationId: initialConversationId,
  recipientId,
  realEstateId,
  isFriend
}: Props) {
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId);
  const {
    isEmailVerified
  } = useAuth();
  const router = useRouter();
  const {
    closeModal
  } = useModal();
  const [isSending, setIsSending] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [replyTo, setReplyTo] = useState<MessageSchemaType | null>(null);
  const [pendingImages, setPendingImages] = useState<{
    id: string;
    file: File;
    previewUrl: string;
  }[]>([]);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const {
    data: sessionData
  } = useSession();
  const {
    socket,
    isConnected,
    ensureConnected
  } = useSocketContext();
  const {
    data: conversationData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isPending,
    isLoading
  } = useConversationById(conversationId);
  const stopTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);
  const hasMarkedAsReadRef = useRef(false);
  const prevMessageCountForReadRef = useRef(0);
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const chatRootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);
  const shouldScrollSmoothRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const lastMessageIdRef = useRef<string | null>(null);
  const {
    keyboardOpen,
    onInputFocus,
    onInputBlur
  } = useKeyboardViewport({
    rootRef: chatRootRef,
    scrollRef: conversationRef
  });
  const messages = useMemo(() => {
    return [...(conversationData?.pages.flatMap(page => page.data.messages) || [])].reverse();
  }, [conversationData]);
  const unreadCount = conversationData?.pages[0].data.conversation.unreadCount || 0;
  const lastMessageReadAt = useMemo(() => {
    return conversationData?.pages[0]?.data?.conversation.lastMessageReadAt;
  }, [conversationData]);
  const otherUserLastReadAt = useMemo(() => {
    return conversationData?.pages[0]?.data?.conversation.otherUserLastReadAt;
  }, [conversationData]);
  const unreadSeparatorIndex = useMemo(() => {
    if (!lastMessageReadAt || messages.length === 0) return -1;
    const firstUnreadIndex = messages.findIndex(msg => new Date(msg.createdAt) > new Date(lastMessageReadAt));
    return firstUnreadIndex > 0 ? firstUnreadIndex - 1 : -1;
  }, [messages, lastMessageReadAt]);
  const firstUnreadMessageId = useMemo(() => {
    if (!lastMessageReadAt || messages.length === 0) return null;
    const firstUnreadMsg = messages.find(msg_0 => new Date(msg_0.createdAt) > new Date(lastMessageReadAt));
    return firstUnreadMsg?.id || null;
  }, [messages, lastMessageReadAt]);
  const unreadOtherUserIndex = useMemo(() => {
    if (!otherUserLastReadAt || messages.length === 0) return -1;
    const firstUnreadIndex_0 = messages.findIndex(msg_1 => new Date(msg_1.createdAt) > new Date(otherUserLastReadAt));
    return firstUnreadIndex_0 > 0 ? firstUnreadIndex_0 - 1 : -1;
  }, [messages, otherUserLastReadAt]);
  const firstUnreadOtherUserMessageId = useMemo(() => {
    if (!otherUserLastReadAt || messages.length === 0) return null;
    const firstUnreadMsg_0 = messages.find(msg_2 => new Date(msg_2.createdAt) > new Date(otherUserLastReadAt));
    return firstUnreadMsg_0?.id || null;
  }, [messages, otherUserLastReadAt]);
  const hasUserAnsweredAfterFirstMessage = useMemo(() => {
    if (!sessionData) return false;
    // Friends are not subject to the wait-for-reply rule. We honor the prop
    // override (set when opening from the friends list) and the conversation
    // payload's flag (covers listing-context chats between friends).
    const conversationIsFriend = conversationData?.pages[0]?.data?.conversation?.isFriend;
    if (isFriend || conversationIsFriend) return false;
    const userMessagesCount = messages.filter(msg_3 => msg_3.senderId === sessionData.id).length;
    const otherUserMessagesCount = messages.filter(msg_4 => msg_4.senderId !== sessionData.id).length;

    // Block the user from sending more than one message when the other user has not replied yet
    return userMessagesCount > 0 && otherUserMessagesCount === 0;
  }, [messages, sessionData, isFriend, conversationData]);
  const handleScrollToBottom = () => {
    const el = conversationRef.current;
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth"
      });
      setNewMessagesCount(0);
    }
  };

  // Initial load: jump to the bottom before the browser paints so the user
  // never sees the thread at the top. The list is kept hidden (isReady) until
  // this runs, then faded in.
  useLayoutEffect(() => {
    if (!isInitialLoadRef.current || messages.length === 0) return;
    const el_0 = conversationRef.current;
    if (el_0) el_0.scrollTop = el_0.scrollHeight;
    isInitialLoadRef.current = false;
    prevMessageCountRef.current = messages.length;
    lastMessageIdRef.current = messages[messages.length - 1].id;
    setIsReady(true);
  }, [messages]);
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    const hasNewMessages = messages.length > prevMessageCountRef.current;
    if (hasNewMessages) {
      const newMessageCount = messages.length - prevMessageCountRef.current;

      // Check if this is a real new message (added at the end) or pagination (added at the beginning)
      // Pagination adds old messages at the beginning, new messages are added at the end
      const lastMessage = messages[messages.length - 1];
      const isRealNewMessage = lastMessage && (!lastMessageIdRef.current || lastMessage.id !== lastMessageIdRef.current);

      // Only auto-scroll if: user sent message, or already at bottom
      const shouldAutoScroll = shouldScrollSmoothRef.current || isAtBottom;
      if (shouldAutoScroll) {
        const behavior = shouldScrollSmoothRef.current ? "smooth" : "instant";
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior,
            block: "end"
          });
          shouldScrollSmoothRef.current = false;
        }, 100);
        setNewMessagesCount(0);
      } else if (isRealNewMessage) {
        // Only increment counter if it's a real new message (not pagination)
        setNewMessagesCount(prev => prev + newMessageCount);
      }
    }

    // Update refs
    prevMessageCountRef.current = messages.length;
    if (messages.length > 0) {
      lastMessageIdRef.current = messages[messages.length - 1].id;
    }
  }, [messages.length, isAtBottom]);
  const handleLoadMoreItems = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
    return;
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const handleScroll = () => {
    const container = conversationRef.current;
    if (!container || messages.length === 0) return;
    const scrollTop = container.scrollTop;
    const isAtBottom_0 = container.scrollHeight - container.scrollTop - container.clientHeight <= SCROLL_DOWN_THRESHOLD_TO_SHOW_BUTTON;
    if (scrollTop < LOAD_MORE_SCROLL_TRIGGER_THRESHOLD && hasNextPage) {
      const prevScrollHeight = container.scrollHeight;
      handleLoadMoreItems().then(() => {
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - prevScrollHeight + scrollTop;
        }, 0);
      });
    }
    setIsAtBottom(isAtBottom_0);

    // Reset new messages count when user scrolls to bottom
    if (isAtBottom_0) {
      setNewMessagesCount(0);
    }
  };
  useEffect(() => {
    const container_0 = conversationRef.current;
    if (container_0) {
      container_0.addEventListener("scroll", handleScroll);
      return () => {
        container_0.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleLoadMoreItems, messages, isPending]);
  useEffect(() => {
    if (!socket || !isConnected || conversationId) return;
    const handleConversationCreated = (data: ConversationCreatedSchemaType) => {
      setConversationId(data.conversation.id);
    };

    // Opened via recipientId onto a conversation that already exists (e.g. a
    // previously-deleted listing thread): the backend returns it as NOT new, so
    // no `conversation_created` fires. `message_sent` still carries the id, so
    // adopt it to load the thread and show the just-sent message.
    const handleMessageSent = (data_0: {
      conversationId?: string;
    }) => {
      if (data_0?.conversationId) setConversationId(data_0.conversationId);
    };
    socket.on(WEBSOCKET_EVENTS.CONVERSATION_CREATED, handleConversationCreated);
    socket.on(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent);
    return () => {
      socket.off(WEBSOCKET_EVENTS.CONVERSATION_CREATED, handleConversationCreated);
      socket.off(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent);
    };
  }, [socket, isConnected, conversationId]);
  useEffect(() => {
    if (!socket || !isConnected || !sessionData || !conversationId) return;
    const unreadMessages = messages.filter(msg_5 => !msg_5.isRead && msg_5.senderId !== sessionData.id);
    const messageCountIncreased = messages.length > prevMessageCountForReadRef.current;
    const shouldMarkAsRead = unreadMessages.length > 0 && (!hasMarkedAsReadRef.current || messageCountIncreased);
    if (shouldMarkAsRead) {
      hasMarkedAsReadRef.current = true;
      prevMessageCountForReadRef.current = messages.length;
      socket.emit(WEBSOCKET_EVENTS.MARK_READ, {
        conversationId
      });
      queryClient.setQueryData(USER_CHAT_UNREAD_COUNT_QUERY_KEYS.getUserChatUnreadCount(sessionData.id), (oldData: Query<ChatMessagesUnreadCountSchemaType>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            count: Math.max(0, oldData.data.count - unreadMessages.length)
          }
        };
      });
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(sessionData?.id), (oldData_0: Query<ChatConversationSchemaType[]>) => {
        if (!oldData_0) return oldData_0;
        return {
          ...oldData_0,
          data: oldData_0.data.map(conversation => {
            if (conversation.id === conversationId) {
              return {
                ...conversation,
                lastMessage: {
                  ...conversation.lastMessage,
                  isRead: true
                },
                unreadCount: 0
              };
            }
            return conversation;
          })
        };
      });
    }
    if (unreadMessages.length === 0 && hasMarkedAsReadRef.current) {
      hasMarkedAsReadRef.current = false;
      prevMessageCountForReadRef.current = messages.length;
    }
  }, [socket, isConnected, sessionData, conversationId, messages.length, queryClient]);
  useEffect(() => {
    if (!socket || !isConnected || !sessionData) return;
    const handleUserTyping = () => {
      setIsOtherUserTyping(true);
    };
    const handleUserStopTyping = () => {
      setIsOtherUserTyping(false);
    };
    socket.on(WEBSOCKET_EVENTS.OTHER_USER_TYPING, handleUserTyping);
    socket.on(WEBSOCKET_EVENTS.OTHER_USER_STOP_TYPING, handleUserStopTyping);
    return () => {
      socket.off(WEBSOCKET_EVENTS.OTHER_USER_TYPING, handleUserTyping);
      socket.off(WEBSOCKET_EVENTS.OTHER_USER_STOP_TYPING, handleUserStopTyping);
    };
  }, [socket, isConnected, sessionData]);
  useEffect(() => {
    if (!inputValue || !isConnected || !socket) {
      // Clear timeout when input is empty
      if (stopTypingTimeoutRef.current) {
        clearTimeout(stopTypingTimeoutRef.current);
        stopTypingTimeoutRef.current = null;
      }
      return;
    }
    const userTypingData: UserTypingSchemaType = {
      conversationId,
      recipientId: conversationData?.pages[0].data?.conversation.otherUser.id
    };

    // Only emit USER_TYPING if not already typing
    if (!isTypingRef.current) {
      socket.emit(WEBSOCKET_EVENTS.USER_TYPING, userTypingData);
      isTypingRef.current = true;
    }

    // Clear previous timeout
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
    }

    // Set new timeout
    stopTypingTimeoutRef.current = setTimeout(() => {
      socket.emit(WEBSOCKET_EVENTS.USER_STOP_TYPING, userTypingData);
      stopTypingTimeoutRef.current = null;
      isTypingRef.current = false; // Reset typing state
    }, TYPING_STOP_DELAY);
    return () => {
      if (stopTypingTimeoutRef.current) {
        clearTimeout(stopTypingTimeoutRef.current);
      }
    };
  }, [inputValue, isConnected, socket, conversationId, conversationData]);
  const addImages = useCallback((files: File[] | FileList) => {
    const imgs = Array.from(files).filter(f => f.type.startsWith("image/") && f.size <= MAX_IMAGE_SIZE);
    if (!imgs.length) return;
    setPendingImages(prev_0 => [...prev_0, ...imgs.map(file => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file)
    }))]);
  }, []);
  const removeImage = useCallback((id: string) => {
    setPendingImages(prev_1 => {
      const found = prev_1.find(p => p.id === id);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev_1.filter(p_0 => p_0.id !== id);
    });
  }, []);
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!socket) return;
    if (!inputValue.trim() && pendingImages.length === 0) return;
    if (!isConnected) ensureConnected();
    if (!conversationId && !recipientId) return; // Need either conversationId or recipientId
    if (hasUserAnsweredAfterFirstMessage) return;

    // Keep reference to input element before state changes
    const inputElement = inputRef.current;
    const contentToSend = inputValue;
    const imagesToSend = pendingImages;
    setIsSending(true);
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
      stopTypingTimeoutRef.current = null;
    }
    if (isTypingRef.current) {
      socket.emit(WEBSOCKET_EVENTS.USER_STOP_TYPING, {
        conversationId,
        recipientId: recipientId || conversationData?.pages[0].data?.conversation.otherUser.id
      });
      isTypingRef.current = false;
    }

    // Clear the composer immediately for a responsive feel.
    setInputValue("");
    setReplyTo(null);
    setPendingImages([]);
    const tempId = Date.now().toString();
    const recipient = recipientId || conversationData?.pages[0].data?.conversation.otherUser.id;

    // Patch the optimistic message (by temp id) in the by-id cache.
    const patchOptimistic = (patch: Partial<MessageSchemaType>) => {
      if (!conversationId) return;
      queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), (oldData_1: any) => {
        if (!oldData_1?.pages?.length) return oldData_1;
        return {
          ...oldData_1,
          pages: oldData_1.pages.map((page_0: any, i: number) => i !== 0 ? page_0 : {
            ...page_0,
            data: {
              ...page_0.data,
              messages: page_0.data.messages.map((m: any) => m.id === tempId ? {
                ...m,
                ...patch
              } : m)
            }
          })
        };
      });
    };

    // Show the message immediately with local image previews + a sending
    // state; the upload/send then reconcile it in place (or mark it failed).
    const optimisticMessage: MessageSchemaType = {
      id: tempId,
      conversationId: conversationId ?? "",
      content: contentToSend,
      senderId: sessionData.id,
      createdAt: new Date().toISOString(),
      isRead: true,
      replyToId: replyTo?.id ?? null,
      replyTo: replyTo ? {
        id: replyTo.id,
        senderId: replyTo.senderId,
        content: replyTo.deleted ? null : replyTo.content,
        deleted: !!replyTo.deleted,
        image: replyTo.deleted ? null : replyTo.attachments?.[0]?.url ?? null
      } : null,
      attachments: imagesToSend.map(p_1 => ({
        id: p_1.id,
        url: p_1.previewUrl
      })),
      // Only images carry a pending/failed state; text is assumed delivered.
      status: imagesToSend.length ? "sending" : undefined
    };
    if (conversationId) {
      queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), (oldData_2: any) => {
        const firstPage = oldData_2?.pages?.[0];
        if (!firstPage) return oldData_2;
        return {
          ...oldData_2,
          pages: [{
            ...firstPage,
            data: {
              ...firstPage.data,
              messages: [optimisticMessage, ...firstPage.data.messages]
            }
          }, ...oldData_2.pages.slice(1)]
        };
      });
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(sessionData.id), (oldData_3: Query<ChatConversationSchemaType[]>) => {
        if (!oldData_3) return oldData_3;
        const exists = oldData_3.data.some(c => c.id === conversationId);
        if (exists) {
          return {
            ...oldData_3,
            data: oldData_3.data.map(conversation_0 => conversation_0.id === conversationId ? {
              ...conversation_0,
              lastMessage: optimisticMessage,
              lastMessageAt: optimisticMessage.createdAt
            } : conversation_0)
          };
        }
        const convInfo = conversationData?.pages[0]?.data?.conversation;
        if (!convInfo) return oldData_3;
        const readded: ChatConversationSchemaType = {
          ...convInfo,
          lastMessage: optimisticMessage,
          lastMessageAt: optimisticMessage.createdAt,
          unreadCount: 0
        };
        return {
          ...oldData_3,
          data: [readded, ...oldData_3.data]
        };
      });
    }
    shouldScrollSmoothRef.current = true;
    inputElement?.focus();
    setTimeout(() => setIsSending(false), 400);

    // Upload images first, reporting progress. We keep displaying the LOCAL
    // preview (never swap to the CDN url) so the image doesn't reload/flicker
    // — a later refetch naturally serves the remote url. The blob is left
    // alive for the session (revoking it would blank the shown image).
    let assetIds: string[] | undefined;
    if (imagesToSend.length) {
      try {
        const res = await uploadChatImagesAction({
          files: imagesToSend.map(p_2 => p_2.file),
          onProgress: percent => patchOptimistic({
            uploadProgress: percent
          })
        });
        assetIds = res.data.map(a => a.id);
        patchOptimistic({
          status: undefined,
          uploadProgress: undefined
        });
      } catch {
        patchOptimistic({
          status: "failed",
          uploadProgress: undefined
        });
        return;
      }
    }

    // Emit with an ack so a failed send (or 15s timeout) flags the message.
    socket.timeout(15000).emit(WEBSOCKET_EVENTS.SEND_MESSAGE, {
      conversationId,
      content: contentToSend,
      realEstateId,
      recipientId: recipient,
      replyToId: replyTo?.id,
      assetIds
    } as NewMessageSchemaType, (err: unknown, ack: {
      success?: boolean;
    } | undefined) => {
      if (err || !ack?.success) patchOptimistic({
        status: "failed"
      });
    });
  }, [socket, isConnected, conversationId, recipientId, conversationData, inputValue, pendingImages, sessionData, queryClient, hasUserAnsweredAfterFirstMessage, replyTo]);
  const deleteMessage = (messageId: string) => {
    if (!socket || !isConnected || !conversationId) return;
    socket.emit(WEBSOCKET_EVENTS.DELETE_MESSAGE, {
      conversationId,
      messageId
    });
    queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), (oldData_4: any) => {
      if (!oldData_4?.pages) return oldData_4;
      return {
        ...oldData_4,
        pages: oldData_4.pages.map((page_1: any) => ({
          ...page_1,
          data: {
            ...page_1.data,
            messages: page_1.data.messages.map((m_0: any) => m_0.id === messageId ? {
              ...m_0,
              deleted: true,
              content: ""
            } : m_0)
          }
        }))
      };
    });
  };
  const scrollToMessage = useCallback((messageId_0: string) => {
    const el_1 = document.getElementById(`chat-msg-${messageId_0}`);
    if (!el_1) return;
    el_1.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    el_1.classList.add("bg-primary/10");
    setTimeout(() => el_1.classList.remove("bg-primary/10"), 1200);
  }, []);
  return {
    inputValue,
    setInputValue,
    replyTo,
    setReplyTo,
    pendingImages,
    addImages,
    removeImage,
    scrollToMessage,
    conversationData,
    sessionData,
    isOtherUserTyping,
    handleSubmit,
    deleteMessage,
    isSending,
    messages,
    inputRef,
    messagesEndRef,
    conversationRef,
    chatRootRef,
    keyboardOpen,
    onInputFocus,
    onInputBlur,
    isFetchingNextPage,
    isLoadingConversation: isLoading && !!conversationId,
    isReady,
    isAtBottom,
    handleScrollToBottom,
    unreadSeparatorIndex,
    unreadOtherUserIndex,
    unreadCount,
    firstUnreadMessageId,
    firstUnreadOtherUserMessageId,
    newMessagesCount,
    isEmailVerified,
    hasUserAnsweredAfterFirstMessage,
    router,
    closeModal
  };
}